import React, { ChangeEvent } from 'react';
import { StepProps, Division, Team } from '../../../types';

const StructureStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const { divisions } = formData;

  const updateDivisions = (newDivisions: Division[]): void => {
    updateFormData({ divisions: newDivisions });
  };

  const addDivision = (): void => {
    updateDivisions([
      ...divisions,
      { name: '', teams: [{ name: '', color: '' }] }
    ]);
  };

  const removeDivision = (divIndex: number): void => {
    if (divisions.length > 1) {
      const newDivisions = divisions.filter((_, i) => i !== divIndex);
      updateDivisions(newDivisions);
    }
  };

  const updateDivision = (divIndex: number, field: keyof Division, value: string | Team[]): void => {
    const newDivisions = [...divisions];
    newDivisions[divIndex] = { ...newDivisions[divIndex], [field]: value };
    updateDivisions(newDivisions);
  };

  const addTeam = (divIndex: number): void => {
    const newDivisions = [...divisions];
    newDivisions[divIndex].teams.push({ name: '', color: '', sponsorName: '' });
    updateDivisions(newDivisions);
  };

  const removeTeam = (divIndex: number, teamIndex: number): void => {
    const newDivisions = [...divisions];
    if (newDivisions[divIndex].teams.length > 1) {
      newDivisions[divIndex].teams = newDivisions[divIndex].teams.filter(
        (_, i) => i !== teamIndex
      );
      updateDivisions(newDivisions);
    }
  };

  const updateTeam = (divIndex: number, teamIndex: number, field: keyof Team, value: string): void => {
    const newDivisions = [...divisions];
    newDivisions[divIndex].teams[teamIndex] = {
      ...newDivisions[divIndex].teams[teamIndex],
      [field]: value
    };
    updateDivisions(newDivisions);
  };

  return (
    <div className="step-content">
      <div className="step-section">
        <h3 className="step-section-title">League Structure</h3>
        <p className="step-section-description">
          Add your divisions/age groups and teams. Each division can have multiple teams.
        </p>

        {divisions.map((division, divIndex) => (
          <div key={divIndex} className="division-card">
            <div className="division-header">
              <div className="form-group">
                <label className="form-label">Division/Age Group Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={division.name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => updateDivision(divIndex, 'name', e.target.value)}
                  placeholder="e.g., 10U, Majors, Varsity"
                />
              </div>
              {divisions.length > 1 && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => removeDivision(divIndex)}
                >
                  Remove Division
                </button>
              )}
            </div>

            <div className="teams-section">
              <div className="teams-header">
                <h4>Teams in this Division</h4>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => addTeam(divIndex)}
                >
                  + Add Team
                </button>
              </div>

              {division.teams.map((team, teamIndex) => (
                <div key={teamIndex} className="team-row">
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-input"
                      value={team.name}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => updateTeam(divIndex, teamIndex, 'name', e.target.value)}
                      placeholder="Team name (e.g., Red Sox, Tigers)"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-input"
                      value={team.color}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => updateTeam(divIndex, teamIndex, 'color', e.target.value)}
                      placeholder="Primary color (e.g., Red, Navy Blue)"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-input"
                      value={team.sponsorName || ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => updateTeam(divIndex, teamIndex, 'sponsorName', e.target.value)}
                      placeholder="Sponsor name (optional)"
                    />
                  </div>
                  {division.teams.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => removeTeam(divIndex, teamIndex)}
                      style={{ alignSelf: 'center' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-outline"
          onClick={addDivision}
        >
          + Add Another Division
        </button>
      </div>
    </div>
  );
};

export default StructureStep;
